import { registerComponentInterceptor } from '../componentInterceptor';
import { default as React, useEffect, useState } from 'react';
import {
  devHubDynamicSvgIcon,
  DevHubIconButton,
} from '@devxdiscover/devhub-ui';
import { usePoll } from '../../util/usePoll.ts';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { cacheDelete, cacheGet, cacheSet } from '../../util/persistedState.ts';
import { ConfigFeatureCheck } from '../../util/ConfigFeatureCheck.tsx';
import { Feature } from '@devxdiscover/devhub-common';

/**
 * Refresh button for the Identity Card
 */
const RefreshButton = () => {
  const identityApi = useApi(identityApiRef);
  const config = useApi(configApiRef);
  const backendUrl = config.getString('backend.baseUrl');
  const [login, setLogin] = useState<string | undefined>(undefined);
  const entityKey = login ? `devhub-identity-refresh:${login}` : undefined;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingRunId, setPendingRunId] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    identityApi.getBackstageIdentity().then(identity => {
      const ref = identity.userEntityRef;
      const userLogin = ref.substring(ref.lastIndexOf('/') + 1);
      setLogin(userLogin);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore persisted refresh state once login (and entityKey) is available
  useEffect(() => {
    if (!entityKey) return;
    const stored = cacheGet<string>(entityKey);
    if (stored) {
      setPendingRunId(stored);
      setIsRefreshing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityKey]);

  usePoll({
    enabled: isRefreshing && !!pendingRunId,
    fn: async () => {
      const response = await fetch(
        `${backendUrl}/api/fusionUtil/isRunning/byUser/${login}`,
      );
      if (!response.ok) throw new Error(response.statusText);
      const { isRunning } = await response.json();
      return !isRunning;
    },
    onDone: () => {
      if (entityKey) cacheDelete(entityKey);
      setPendingRunId(undefined);
      setIsRefreshing(false);
    },
  });

  const handleRefresh = async () => {
    if (!entityKey) return;
    setIsRefreshing(true);
    try {
      const response = await fetch(`${backendUrl}/api/fusionUtil/import/user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'identity' }),
      });
      if (!response.ok)
        throw new Error(`Failed to refresh: ${response.statusText}`);
      cacheSet(entityKey, entityKey, { minutes: 30 });
      setPendingRunId(entityKey);
    } catch {
      setIsRefreshing(false);
    }
  };

  return (
    <ConfigFeatureCheck feature={Feature.ONDEMAND_REFRESH_USER}>
      <DevHubIconButton
        onClick={handleRefresh}
        disabled={isRefreshing || !login}
        aria-label="refresh"
        title="Refresh"
      >
        {devHubDynamicSvgIcon('Refresh')()}
      </DevHubIconButton>
    </ConfigFeatureCheck>
  );
};

registerComponentInterceptor({
  canIntercept(_type: React.ElementType, props: any, _key: React.Key) {
    return props?.title === 'Backstage Identity' ? true : false;
  },
  performIntercept(type: React.ElementType, props: any, key: React.Key) {
    props.title = 'DevHub Identity';
    // Add refresh button as an action in the top right
    props.action = <RefreshButton />;
    return {
      jsxFnProps: {
        type,
        props,
        key,
      },
    };
  },
});
